import React from "react";

const DisclaimerPage = () => {
  return (
    <div className="pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="space-y-8">
          {/* Header Section - matches landing page legal style */}
          <header className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-crimson">Legal</p>
            <h1 className="mt-3 font-heading text-4xl font-bold text-foreground sm:text-5xl">
              Disclaimer
            </h1>
            <p className="mt-4 text-muted-foreground">
              Last Updated: January 2024
            </p>
          </header>

          <div className="prose dark:prose-invert max-w-none space-y-12">
            {/* Introduction */}
            <div className="space-y-6">
              <div className="rounded-xl border border-border bg-card p-6">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Please read this disclaimer carefully before using Cold Case Game
                  Kits.
                </p>
              </div>

              {/* Table of Contents - matches landing card style */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Table of Contents</h2>
                <nav className="grid gap-2 text-sm">
                  <a href="#fictional" className="text-crimson hover:underline">
                  1. Fictional Content
                  </a>
                  <a href="#educational" className="text-crimson hover:underline">2. Educational Purpose</a>
                  <a href="#content-warning" className="text-crimson hover:underline">3. Content Warning</a>
                  <a href="#age" className="text-crimson hover:underline">4. Age Appropriateness</a>
                  <a href="#technical" className="text-crimson hover:underline">5. Technical Disclaimer</a>
                  <a href="#third-party" className="text-crimson hover:underline">6. Third-Party Content</a>
                  <a href="#user" className="text-crimson hover:underline">7. User Responsibility</a>
                  <a href="#changes" className="text-crimson hover:underline">8. Changes to Content</a>
                  <a href="#contact" className="text-crimson hover:underline">9. Contact Information</a>
                </nav>
              </div>
            </div>

          {/* Main Content Sections */}
          <section id="fictional" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">1. Fictional Content</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                All mystery games, stories, characters, and events presented on
                Cold Case Game Kits are entirely fictional. Any resemblance to
                real persons, living or dead, or actual events is purely
                coincidental.
              </p>
            </div>
          </section>

          <section id="educational" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">2. Educational Purpose</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                Our games are designed for entertainment purposes only. They are not intended to provide professional
                investigative training or legal advice.
              </p>
            </div>
          </section>

          <section id="content-warning" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">3. Content Warning</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">
                Our mystery games may contain mature themes, including but not
                limited to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>References to criminal activities</li>
                <li>Descriptions of crime scenes</li>
                <li>Mentions of violence </li>
                <li>Complex moral situations</li>
              </ul>
            </div>
          </section>

          <section id="age" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">4. Age Appropriateness</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                While our content is generally suitable for teenagers and
                adults, parental guidance is recommended for users under 16
                years of age.
              </p>
            </div>
          </section>

          <section id="technical" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">5. Technical Disclaimer</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">
                We strive to ensure our games function properly across all
                supported platforms, but we cannot guarantee:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Uninterrupted access to our services</li>
                <li>Compatibility with all devices or browsers</li>
                <li>Freedom from technical errors or bugs</li>
                <li>Immediate resolution of technical issues</li>
              </ul>
            </div>
          </section>

          <section id="third-party" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">6. Third-Party Content</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                Some game kits may include links to third-party websites or
                resources. We are not responsible for the content or
                availability of these external resources.
              </p>
            </div>
          </section>

          <section id="user" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">7. User Responsibility</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p className="mb-4">Users are responsible for:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Ensuring their device meets minimum requirements</li>
                <li>Ensuring their device can run the game kits</li>
                <li>Backing up their game kits</li>
                <li>Using the platform in accordance with our terms</li>
              </ul>
            </div>
          </section>

          <section id="changes" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">8. Changes to Content</h2>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 text-muted-foreground">
              <p>
                We reserve the right to modify, update, or remove game kit content
                without prior notice to maintain quality and improve user
                experience.
              </p>
            </div>
          </section>

          <section id="contact" className="scroll-mt-16 space-y-6">
            <div className="flex items-center gap-2 border-b border-border pb-2">
              <h2 className="font-heading text-2xl font-bold text-foreground">9. Contact Information</h2>
            </div>
            <div className="rounded-xl border border-crimson/20 bg-crimson/5 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
                Questions about this disclaimer?
              </h3>
              <address className="not-italic space-y-2 text-muted-foreground">
                <p className="font-medium text-foreground">DGF Digital Inc.</p>
                <p>9-2020 Lanthier Drive Suite 111</p>
                <p>Orleans, ON K4A 3V4</p>
                <p className="mt-4">
                  <span className="font-medium text-foreground">Email: </span>
                  <a
                    href="mailto:legal@coldcasegamekits.com"
                    className="text-crimson hover:underline"
                  >
                    contact@dgfdigital.com
                  </a>
                </p>
              </address>
            </div>
          </section>

          {/* Footer - matches landing legal footer */}
          <div className="border-t border-border pt-8 mt-12 text-center text-sm text-muted-foreground space-y-2">
            <p>Effective Date: January 1, 2024</p>
            <p>Last Updated: January 20, 2024</p>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerPage;
